/**
 * @typedef {{ type: 'text', text: string }} TextSegment
 * @typedef {{ type: 'tag', name: string, id: string }} TagSegment
 * @typedef {TextSegment | TagSegment} TranscriptSegment
 */

export function newChipId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `chip-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  );
}

/** @param {unknown} segments @returns {TranscriptSegment[]} */
export function normalizeSegments(segments) {
  if (!Array.isArray(segments) || segments.length === 0) {
    return [{ type: 'text', text: '' }];
  }
  const mapped = segments.map((s) => {
    if (s?.type === 'tag') {
      return {
        type: 'tag',
        name: String(s.name ?? '').trim(),
        id: s.id ? String(s.id) : newChipId(),
      };
    }
    return { type: 'text', text: String(s.text ?? '') };
  });
  const merged = [];
  for (const s of mapped) {
    if (s.type === 'text') {
      const last = merged[merged.length - 1];
      if (last?.type === 'text') last.text += s.text;
      else merged.push({ type: 'text', text: s.text });
    } else if (s.name) merged.push(s);
  }
  if (merged.length === 0) return [{ type: 'text', text: '' }];
  return merged;
}

/**
 * Build plain text from transcript segments.
 * By default each tag becomes ` [TagName] `; callers can disable brackets.
 *
 * @param {TranscriptSegment[]} segments
 * @param {{ wrapTagsInBrackets?: boolean }} [opts]
 */
export function segmentsToPlainText(segments, opts = {}) {
  const wrapTagsInBrackets = opts?.wrapTagsInBrackets !== false;
  if (!Array.isArray(segments) || segments.length === 0) return '';
  return normalizeSegments(segments)
    .map((s) => {
      if (s.type === 'text') return s.text ?? '';
      if (s.type === 'tag') {
        const n = String(s.name ?? '').trim();
        if (!n) return '';
        if (!wrapTagsInBrackets) return ` ${n} `;
        const alreadyBracketWrapped = /^\[.*\]$/.test(n);
        return alreadyBracketWrapped ? ` ${n} ` : ` [${n}] `;
      }
      return '';
    })
    .join('');
}

/** @param {string} plain */
export function defaultSegmentsFromPlain(plain) {
  const t = typeof plain === 'string' ? plain : '';
  return normalizeSegments([{ type: 'text', text: t }]);
}

/** First-seen order of tag names in the document. */
export function uniqueTagNamesInOrder(segments) {
  const out = [];
  const seen = new Set();
  for (const s of segments || []) {
    if (s.type === 'tag' && s.name && !seen.has(s.name)) {
      seen.add(s.name);
      out.push(s.name);
    }
  }
  return out;
}

export function segmentsEqual(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}
